import { browserHistory } from '../helpers/components';
import { setSearchBookings } from '../actions/searchBookings';
import { USAGE_TYPE_BUSINESS, USAGE_TYPE_PRIVATE } from '../constants/backend';
import _isString from 'lodash/isString';
import endpoints from '../constants/endpoints';

import {
  popup_BookingRecap_SetVisibility,
  popup_Generic_SetVisibility
} from './popup';
import {
  brandIdSelector,
  canLinkProfileBasedOnIds,
  localeSelector,
  messagesSelector,
  profileCompanyIdsSelector,
  searchValuesSelector,
  userCompanyIdSelector,
  userIdSelector
} from '../helpers/selectors';

import {
  addQuery,
  getMessage as t,
  isBookingPage,
  isCreationPage,
  isHomePage,
  isRMF_companyId,
  isSubscribePage,
  navigateToCreationPage,
  navigateToWelcomePage,
  safe
} from './../utils/utils';

import { ONE_WAY } from '../constants/generic';
import { addErrorMsg } from '../utils/flashMessage/creator';
import { EXTERNAL_REGISTRATION_URL } from '../helpers/ssoRegistrationFlow';
import { getPreBookingStatus, search } from './booking';
import { formatDataForSearchBookingsApiCall, setMomentTimeForRequest } from '../helpers';
import { SERVER_ERROR } from '../helpers/errors';
import { noTokensFound, parseAuthTokensFromApi, saveAuthTokens } from '../utils/tokens';

function handleUpdateBookingSearch(userData) {
  return (dispatch, getState) => {
    if (!(isHomePage() || isBookingPage())) return;

    const state = getState();
    const { company } = userData || {};
    const { contract, id: companyId } = company || {};
    const { businessCarSharing, privateCarSharing, interfaceConfig } = contract || {};
    const { returnTripDisplay } = interfaceConfig || {};
    const searchValues = searchValuesSelector(state);
    const { usageTypes, endDate } = searchValues || {};
    const isRecapPopupVisible = state.popup.type_BookingRecap_IsVisible;
    const countryISO = safe(() => searchValues.location.countryISO);
    const mapCountries = safe(() => state.brand.branded.mapCountries) || {};
    const remoteCompany = mapCountries[countryISO] || {};
    const remoteCompanyId = remoteCompany.companyId;

    if (searchValues) {
      if (!returnTripDisplay && _isString(endDate)) {
        dispatch(setSearchBookings({ endDate: null, endTime: null, tripType: ONE_WAY }));
      }

      if (!businessCarSharing && usageTypes === USAGE_TYPE_BUSINESS) {
        dispatch(setSearchBookings({ usageTypes: USAGE_TYPE_PRIVATE }));
      }

      if (!privateCarSharing && usageTypes === USAGE_TYPE_PRIVATE) {
        dispatch(setSearchBookings({ usageTypes: USAGE_TYPE_BUSINESS }));
      }

      if (countryISO && companyId && companyId !== remoteCompanyId) {
        const formattedData = formatDataForSearchBookingsApiCall(
          setMomentTimeForRequest(searchValues),
          companyId,
          contract
        );

        dispatch(search(formattedData));
      }

      if (isRecapPopupVisible) {
        dispatch(popup_BookingRecap_SetVisibility(false));
      }
    }
  };
}

function handleNoContract(msgKey) {
  return (dispatch, getState) => {
    const m = getState().i18n.messages;
    const genericPopupText = t(m, msgKey);

    dispatch(popup_Generic_SetVisibility(true, { genericPopupText }));
    dispatch(logout(true));
  };
}

function handleCompanyContract(userData) {
  return (dispatch) => {
    const { company } = userData || {};
    const { contract } = company || {};
    const { businessCarSharing, privateCarSharing } = contract || {};

    if (!contract) {
      dispatch(handleNoContract('error.contract.not-found'));
      return;
    }

    if (!businessCarSharing && !privateCarSharing) {
      dispatch(handleNoContract('error.contract.inactive'));
      return;
    }

    dispatch(handleUpdateBookingSearch(userData));
  };
}

export function handleLoginSuccess(resp) {
  return (dispatch) => {
    dispatch(handleCompanyContract(resp.payload.data || {}));
  };
}

export function logout(redirect = true) {
  return {
    type: 'user/LOGOUT',
    redirect
  };
}

export function removeUpdateErrors() {
  return {
    type: 'user/REMOVE_UPDATE_ERRORS'
  };
}

// Every post-Keycloak registration goes to the JWT-contract endpoint: the server
// takes the e-mail from the X-AUTH-TOKEN, the body carries no `login`. The URL lives
// in the flow module as a single constant (see EXTERNAL_REGISTRATION_URL).
export function registerExternalUser(userData) {
  return {
    type: 'user/REGISTER_SSO',
    payload: {
      request: {
        method: 'post',
        url: EXTERNAL_REGISTRATION_URL,
        data: userData
      }
    }
  };
}

// A burst of parallel 401s (one page firing several requests) reads as one
// event: the latch keeps it to a single flash, and a session expiring again
// later flashes again.
let accessDeniedFlashedAt = 0;

const ACCESS_DENIED_FLASH_DEDUP_MS = 3000;

// Both access-denied paths land the same way: flash the session-expired message
// and go home - a flash, not a modal, and home is reached unconditionally (a
// logged-out visitor left on a protected route would render nothing).
// Deliberately NO automatic Keycloak redirect: the user restarts the login from
// the header when he decides to.
function explainAccessDenied() {
  return (dispatch, getState) => {
    if (Date.now() - accessDeniedFlashedAt > ACCESS_DENIED_FLASH_DEDUP_MS) {
      accessDeniedFlashedAt = Date.now();
      addErrorMsg(messagesSelector(getState())['error.session.expired']);
    }

    if (window.location.pathname !== '/') browserHistory.push('/');
  };
}

// The Authenticated guard concluded "anonymous on a protected route". With tokens
// in memory a login is still settling - stay quiet and let it finish.
export function redirectIfNotAuthenticated() {
  return (dispatch) => {
    if (noTokensFound()) dispatch(explainAccessDenied());
  };
}

// The axios interceptor gave up on a 401/403 after its one reactive refresh:
// the session is over. FINAL_ACCESS_DENIED tears the local session data down.
export function handleSessionExpired() {
  return (dispatch) => {
    dispatch(explainAccessDenied());
    return dispatch({ type: 'user/FINAL_ACCESS_DENIED' });
  };
}

export function sendActivationEmail(login) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/SEND_ACTIVATION_EMAIL',
      payload: {
        request: {
          method: 'post',
          url:
            '/v2/users/validate?login=' +
            encodeURIComponent(login) +
            '&brandId=' +
            brandIdSelector(getState())
        }
      }
    });
  };
}

// noinspection JSUnusedGlobalSymbols
export function accountActivation(token) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/ACCOUNT_ACTIVATION',
      payload: {
        request: {
          method: 'put',
          url: '/v2/users/validate?subscriptionToken=' + encodeURIComponent(token)
        }
      }
    });
  };
}

export function getUserInfo(silent) {
  return {
    silent,
    type: 'user/GET_INFO',
    payload: {
      request: {
        method: 'get',
        url: endpoints.me
      }
    }
  };
}

export function getUserSettings(userId) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/GET_SETTINGS',
      payload: {
        request: {
          method: 'get',
          url: `/v2/members/${userId}/settings`
        }
      }
    });
  };
}

// noinspection JSUnusedGlobalSymbols
export function updateUserSettings(userId, data) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/UPDATE_SETTINGS',
      payload: {
        request: {
          method: 'put',
          url: `/v2/members/${userId}/settings`,
          data
        }
      }
    });
  };
}

export function updateInformations(userId, data) {
  return (dispatch, getState) => {
    const state = getState();

    if (!data.locale) data = { ...data, locale: localeSelector(state) };

    return dispatch({
      type: 'user/UPDATE_INFO',
      payload: {
        request: {
          method: 'put',
          url: '/v2/members/' + encodeURIComponent(userId),
          data
        }
      }
    })
      .then((data) => data)
      .catch((error) => error);
  };
}

export function linkUserProfile(data) {
  return (dispatch, getState) => {
    const state = getState();

    if (!data.locale) data = { ...data, locale: localeSelector(state) };

    return dispatch({
      type: 'user/LINK_PROFILE',
      payload: {
        request: {
          method: 'post',
          url: '/v2/members/me/profiles',
          data
        }
      }
    })
      .then((data) => data)
      .catch((error) => error);
  };
}

export function switchUserProfile(profileId) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/SWITCH_PROFILE',
      payload: {
        request: {
          method: 'post',
          url: `/v2/members/${profileId}/activate`
        }
      }
    })
      .then((data) => data)
      .catch((error) => error);
  };
}

export function sendConfirmationEmail(data) {
  return {
    type: 'user/SEND_CONFIRMATION_EMAIL',
    payload: {
      request: {
        method: 'post',
        url: `/v2/users/emails/confirmation${addQuery(data)}`
      }
    }
  };
}

export function selfAnonymize(redirect = true) {
  return {
    redirect,
    type: 'user/SELF_ANONYMIZE',
    payload: {
      request: {
        method: 'post',
        url: `/v2/members/me/request-anonymization`
      }
    }
  };
}

// noinspection JSUnusedGlobalSymbols
export function setRegisterUserData(data) {
  return {
    type: 'user/SET_REGISTER_USER_DATA',
    payload: data
  };
}

export function autoUpdateRegisterIncompleteStatus() {
  return {
    type: 'user/AUTO_UPDATE_REGISTER_INCOMPLETE_STATUS'
  };
}

export function setRegisterIncompleteStatus(status) {
  return {
    type: 'user/SET_REGISTER_INCOMPLETE_STATUS',
    status
  };
}

export function getRegisterCompany(id) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/GET_REGISTER_COMPANY',
      payload: {
        request: {
          method: 'get',
          url: '/v2/companies/' + id
        }
      },
      companyId: id
    });
  };
}

export function getBrandCompanies(brandId) {
  return (dispatch) => {
    return dispatch({
      type: 'user/GET_BRAND_COMPANIES',
      payload: {
        request: {
          method: 'get',
          // same page size the mobile app requests - the backend's default page
          // would under-deliver the list the cascade decides on
          url: `/v2/brands/${brandId}/super-companies/public?number=1&size=1000`
        }
      }
    });
  };
}

export function getCompany(id) {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/GET_COMPANY_FOR_BRAND',
      payload: {
        request: {
          method: 'get',
          url: '/v2/companies/' + id
        }
      },
      companyId: id
    });
  };
}

function getUserAfterRefresh(token) {
  return (dispatch) => {
    return dispatch({
      type: 'user/REFRESH',
      payload: {
        request: {
          method: 'get',
          headers: { 'x-auth-token': token },
          url: endpoints.me
        }
      }
    });
  };
}

function getRefreshToken(token, { useLegacyRefresh = false } = {}) {
  return {
    type: 'user/NORMAL_REFRESH',
    payload: {
      request: {
        method: 'post',
        headers: { [useLegacyRefresh ? 'x-auth-token' : 'x-refresh-token']: token },
        url: endpoints.refresh
      }
    }
  };
}

export function loginWithRefreshToken(token, { useLegacyRefresh = false } = {}) {
  return (dispatch) => {
    return dispatch(getRefreshToken(token, { useLegacyRefresh })).then((data) => {
      if (data.type === 'user/NORMAL_REFRESH_SUCCESS') {
        const tokens = parseAuthTokensFromApi(data.payload.headers);

        saveAuthTokens(tokens);
        return dispatch(getUserAfterRefresh(tokens.token));
      } else {
        return data;
      }
    });
  };
}

export function loginWithAuthToken(token) {
  return (dispatch) => dispatch(getUserAfterRefresh(token));
}

export function updateTokenSilent(token) {
  return {
    type: 'user/SILENT_REFRESH',
    payload: {
      request: {
        method: 'post',
        headers: { 'x-refresh-token': token },
        url: endpoints.refresh
      }
    }
  };
}

export function saveRegisterInnerStateData(data) {
  return {
    type: 'user/SAVE_REGISTER_INNER_STATE_DATA',
    data
  };
}

export function clearRegisterInnerStateData() {
  return {
    type: 'user/CLEAR_REGISTER_INNER_STATE_DATA'
  };
}

export function saveCreationSuccessData(data) {
  return {
    type: 'user/SAVE_CREATION_SUCCESS_DATA',
    data
  };
}

export function clearCreationSuccessData() {
  return {
    type: 'user/CLEAR_CREATION_SUCCESS_DATA'
  };
}

export function clearRegisterCompanyData() {
  return {
    type: 'user/CLEAR_REGISTER_COMPANY_DATA'
  };
}

export function clearProfileFormData() {
  return {
    type: 'user/CLEAR_PROFILE_FORM_DATA'
  };
}

export function checkInviteCode(shortId, keepFetching) {
  return {
    type: 'user/CHECK_INVITE_CODE',
    keepFetching,
    payload: {
      request: {
        method: 'get',
        // lowercased before the request, the normalisation the mobile app also applies
        url: '/v2/companies/invitation/' + shortId.toLowerCase()
      }
    }
  };
}

export function checkInviteCodeStopFetch() {
  return {
    type: 'user/CHECK_INVITE_CODE_FETCH_DONE'
  };
}

export function addInvitError(error) {
  return {
    type: 'user/CHECK_INVITE_CODE_ERROR',
    error
  };
}

export function removeInviteError() {
  return {
    type: 'user/CHECK_INVITE_CODE_CLEAN_ERROR'
  };
}

/**
 * Everything a company reached through an invitation code must NOT be for the user to
 * be allowed to register into it. Evaluated in order, the first failing rule wins and
 * its errorCode is what the invite form displays.
 */
const INVITE_COMPANY_RULES = [
  {
    errorCode: 'member.profile.different.brand',
    fails: (company, state) => safe(() => company.brand.id) !== brandIdSelector(state)
  },
  {
    errorCode: 'info.account.company.already.linked',
    fails: (company, state) =>
      !canLinkProfileBasedOnIds(company.id, profileCompanyIdsSelector(state))
  }
];

function findBrokenInviteRule(company, state) {
  return INVITE_COMPANY_RULES.find((rule) => rule.fails(company, state));
}

/**
 * Resolves an invitation code to the company the user may register into: looks the code
 * up, fetches that company (the lookup response does not carry the brand) and runs
 * INVITE_COMPANY_RULES over it. Resolves with the company id on success, with undefined
 * on failure — in which case the matching invite error has already been dispatched and
 * the form shows it.
 *
 * A request that fails never produces a claim about the code: either call falling over
 * reports SERVER_ERROR, so a server outage is not shown as an invalid code.
 *
 * Validation only: closing popups and navigating stay with the caller.
 */
export function resolveInviteCompany(shortId) {
  return (dispatch, getState) => {
    const failWith = (error) => {
      dispatch(addInvitError(error));
      return undefined;
    };

    // keepFetching keeps the form in its loading state while the company is fetched, and
    // the closing finally is the only place that state is cleared — no exit path, not
    // even a throwing one, can leave the form spinning.
    return dispatch(checkInviteCode(shortId, true))
      .then((invite) => {
        // a failed lookup has already put its own error in the store
        if (invite.type !== 'user/CHECK_INVITE_CODE_SUCCESS') return undefined;

        const companyId = safe(() => invite.payload.data.id);

        if (!companyId) return failWith(SERVER_ERROR);

        return dispatch(getCompany(companyId)).then((resp) => {
          const company = safe(() => resp.payload.data);

          // no company came back: the request failed, which says nothing about the code
          if (!company) return failWith(SERVER_ERROR);

          const brokenRule = findBrokenInviteRule(company, getState());

          if (brokenRule) return failWith({ errorCode: brokenRule.errorCode });

          return companyId;
        });
      })
      .finally(() => dispatch(checkInviteCodeStopFetch()));
  };
}

export function setFlagToOpenRenaultFrancePopup(flag) {
  return { type: 'user/SET_FLAG_TO_OPEN_RENAULT_FRANCE_POPUP', payload: flag };
}

export function getUserProfiles() {
  return (dispatch, getState) => {
    return dispatch({
      type: 'user/GET_PROFILES',
      payload: {
        request: {
          method: 'get',
          url: '/v2/members/me/profiles'
        }
      }
    });
  };
}

export function handleIncompleteUserRedirect(userData) {
  return (dispatch, getState) => {
    const state = getState();
    const { id: userId = userIdSelector(state), company } = userData || {};
    const { id: userCompanyId = userCompanyIdSelector(state) } = company || {};

    if (!isSubscribePage() && !isCreationPage() && !isRMF_companyId(userCompanyId)) {
      dispatch(getPreBookingStatus(userId)).then((status) => {
        if (status) {
          navigateToWelcomePage();
        } else {
          navigateToCreationPage(userCompanyId);
        }
      });
    }
  };
}

export function setSubscribeLoaderState(payload) {
  return {
    type: 'user/SET_SUBSCRIBE_LOADER_STATE',
    payload
  };
}
